import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { isEqual } from 'lodash';
import { PlusOutlined } from '@ant-design/icons';
import {
  findGuarantyListByAnyRef,
  FindGuarantyListByAnyRefParams,
  GuarantyListSearchResponse,
} from 'services/guaranty';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { Button, Form, Input, Typography } from 'antd';
import { indexBy } from 'utils/utils2';
import DF from 'DisplayFramework';
import RouterLink from 'components/RouterLink';
import TasksListPopover from 'pages/workflow/TasksListPopover';
import { useNavigate } from 'react-router-dom';
import GetLabel from 'DisplayFramework/components/GetLabel';
import IActions from 'icons/IActions';
import ButtonComponent from 'components/Button';

type Values = {
  guaranty: GuarantyListSearchResponse[];
};
type GuarantyListProps = {
  onSelect?: (item: GuarantyListSearchResponse) => void;
};
const NEW_GUARANTY_ID = -1;

const GuarantyListModal = (props: GuarantyListProps) => {
  const [form] = Form.useForm();
  const fieldName = 'guaranty';
  const navigate = useNavigate();
  const [searchs, setsearchs] = useState(false);

  const [searchParams, setSearchParams] =
    useState<FindGuarantyListByAnyRefParams>({
      guarcode: '',
      beneficiaryname: '',
      beneficiaryreference: '',
      ctreference: '',
      guarantorname: '',
      guarreference: '',
      guarstatus: '',
      guartype: '',
      propreference: '',
      refguarantor: '',
      tpidbeneficiary: 0,
      tpidguarantor: 0,
    });

  const { data, refetch, isFetching } = useQuery(
    ['findGuarantyListByAnyRef', searchParams],
    () => findGuarantyListByAnyRef(searchParams).then((res) => res.list || []),
    { enabled: searchs }
  );

  // const { add } = useFormList(form, fieldName, {
  //   idKey: 'guarid',
  //   addAsFirst: true,
  // });

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ [fieldName]: data });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data]);

  const getName = useCallback(
    (name: string) => (record: GuarantyListSearchResponse) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions, 'guarid')[record.guarid];

      return [fieldName, index, name];
    },
    [form]
  );
  const { onSelect } = props;

  const columns = useMemo<DataGridColDef<GuarantyListSearchResponse>[]>(
    () => [
      {
        dataIndex: 'guarreference',
        filtrable: true,
        width: 120,

        render: (value, record) => {
          if (onSelect) {
            return (
              <Typography.Link onClick={() => onSelect(record)}>
                {value}
              </Typography.Link>
            );
          } else
            <Form.Item noStyle name={getName('guarreference')(record)}>
              <RouterLink to={`/guaranty/${record.guarid}`}>{value}</RouterLink>
            </Form.Item>;
        },
      },
      {
        dataIndex: 'guarid',
        width: 'hide',
        render: (value, record) => {
          return record.guarid === NEW_GUARANTY_ID ? (
            <Form.Item
              style={{
                display: 'none',
              }}
              name={getName('beneficiaryname')(record)}
            >
              <Input />
            </Form.Item>
          ) : (
            {}
          );
        },
      },
      {
        dataIndex: 'beneficiaryname',
        filtrable: true,
        width: 160
      },
      {
        dataIndex: 'beneficiaryreference',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('beneficiaryreference')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 120,
      },
      {
        dataIndex: 'ctreference',
        filtrable: true,
        width: 120,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('ctreference')(record)}>
              {value}
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'ctrid',
        filtrable: true,
        width: 120,

        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('ctrid')(record)}>
              {value}
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'guaramount',
        filtrable: true,
        width: 120,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('guaramount')(record)}>
              {value}
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'guarantorname',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('guarantorname')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 160,
      },
      {
        dataIndex: 'guardescription',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('guardescription')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 120,
      },

      {
        dataIndex: 'guarremainamount',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('guarremainamount')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 120,
      },
      {
        dataIndex: 'guarsignaturedate',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('guarsignaturedate')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 120,
      },
      {
        dataIndex: 'guarstatus',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('guarstatus')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 120,
      },
      {
        dataIndex: 'guartype',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('guartype')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 120,
      },
      {
        dataIndex: 'refguarantor',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('refguarantor')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 120,
      },
      {
        dataIndex: 'statuslabel',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('statuslabel')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 120,
      },
      {
        dataIndex: 'tpidbeneficiary',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('tpidbeneficiary')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 120,
      },
      {
        dataIndex: 'tpidguarantor',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('tpidguarantor')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 120,
      },
      {
        dataIndex: 'typelabel',
        filtrable: true,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('typelabel')(record)}>
              {value}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        key: 'actions',
        comcode: 'actions',
        fixed: 'right',
        width: 100,
        render: (_, record) => (
          <TasksListPopover
            entityId={record.ctrid}
            entityReference={record?.guarreference}
            entityName="PROPOSAL"
            title={record?.guarreference + ' - ' + record?.beneficiaryname}
            onClose={refetch}
          >
            <Button
              icon={<IActions height="15px" width="15px" />}
              size="middle"
            >
              <GetLabel label="actions" />
            </Button>
          </TasksListPopover>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getName, refetch]
  );
  const row: GuarantyListSearchResponse = {
    guarcode: '',
    beneficiaryname: '',
    beneficiaryreference: '',
    ctreference: '',
    ctrid: 0,
    guaramount: 0,
    guarantorname: '',
    guardescription: '',
    guarexternalref: '',
    guarid: NEW_GUARANTY_ID,
    guarreference: '',
    guarremainamount: 0,
    guarsignaturedate: '',
    guarstatus: '',
    guartype: '',
    propreference: '',
    refguarantor: '',
    statuslabel: '',
    tpidbeneficiary: 0,
    tpidguarantor: 0,
    typelabel: '',
  };
  // const addRow = () => {
  //   add(row);
  // };
  return (
    <DF.Provider pagcode="guaranty">
      <Form form={form} initialValues={row}>
        <Form.Item
          noStyle
          shouldUpdate={(prevValue: Values, nextValue: Values) => {
            return !isEqual(prevValue, nextValue);
          }}
        >
          {({ getFieldValue }) => {
            const Guaranties: GuarantyListSearchResponse[] =
              getFieldValue(fieldName);
            const isNewItemInTable =
              Guaranties?.find(({ guarid }) => guarid === NEW_GUARANTY_ID) !==
              undefined;
            return (
              <DataGrid
                rowKey="guarid"
                dataSource={Guaranties}
                titleComcode="guaranty"
                columns={columns}
                onSearch={(values) => {
                  setSearchParams(values);
                  setsearchs(true);
                }}
                initialSearchValues={searchParams}
                isLoading={isFetching}
                hideExtraAction={!!onSelect}
                extraActions={
                  <ButtonComponent
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/guaranty/create')}
                    disabled={isNewItemInTable}
                    comcode="add"
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

export default GuarantyListModal;
